import { ControlAccessPanel } from "../components/ControlAccessPanel";
import { useCallback, useEffect, useRef, useState } from "react";
import { useStore } from "zustand";

import { useAppConfig } from "../hooks/useAppConfig";
import type { UseAppConfigResult } from "../hooks/useAppConfig";
import { useCameraSlots } from "../hooks/useCameraSlots";
import { useConnectionGate, isSessionEstablished } from "../hooks/useConnectionGate";
import { useOperatorSession } from "../hooks/useOperatorSession";
import { useOperatorInput } from "../hooks/useOperatorInput";
import { useRobot } from "../hooks/useRobot";
import { useRobotState } from "../hooks/useRobotState";
import { deriveStatusText } from "../lib/session-status-labels";
import { useOperatorLogs, SystemLogPanel, DataChannelLogs } from "../modules/teleop-logs";
import { ConnectionSplash } from "../components/ConnectionSplash";
import { RobotStateProvider } from "../components/RobotStateProvider";
import { OperatorHeader } from "../components/OperatorHeader";
import { VideoGrid } from "../components/VideoGrid";
import { InputSourcePanel } from "../components/InputSourcePanel";
import { RobotModelPanel } from "../components/RobotModelPanel";
import { GoalPanel } from "../components/GoalPanel";
import { SessionPhase } from "../modules/teleop-session";
import { XR_SOURCE_ID } from "../modules/teleop-input";
import type { TeleopSession } from "../modules/teleop-session";
import type { OperatorProtocol, TeleopCommands } from "../modules/teleop-commands";
import type { XrVideoSource } from "../modules/teleop-xr";
import styles from "./ViewerPage.module.scss";

export function ViewerPage({ monitorOnly = false, xrInputOnly = false }: {
  monitorOnly?: boolean;
  xrInputOnly?: boolean;
}) {
  const config = useAppConfig();
  const logs = useOperatorLogs();
  // ViewerPage owns the only connection in its tab — persist the peerId so a page
  // reload reconnects as the same operator instead of a fresh one.
  const { session, robotState, commands, protocol } = useOperatorSession(config, {
    log: logs.log,
    persistPeerId: true,
    receiveVideo: true,
  });

  return (
    <RobotStateProvider robotState={robotState}>
      <ViewerBody
        config={config}
        session={session}
        commands={commands}
        protocol={protocol}
        logs={logs}
        monitorOnly={monitorOnly}
        xrInputOnly={xrInputOnly}
      />
    </RobotStateProvider>
  );
}

interface ViewerBodyProps {
  config: UseAppConfigResult;
  session: TeleopSession;
  commands: TeleopCommands;
  protocol: OperatorProtocol;
  logs: ReturnType<typeof useOperatorLogs>;
  monitorOnly: boolean;
  xrInputOnly: boolean;
}

function ViewerBody({ config, session, commands, protocol, logs, monitorOnly, xrInputOnly }: ViewerBodyProps) {
  const robotState = useRobotState();
  const sessionPhase = useStore(session, (s) => s.phase);
  const controlAccess = useStore(session, (s) => s.controlAccess);
  const accessGranted = config.status !== "ready" || !config.controlAuthRequired || controlAccess === "authorized";
  const [videoInHeadset, setVideoInHeadset] = useState(true);
  const [primaryXrCamera, setPrimaryXrCamera] = useState<string>("front");
  const xrVideoSources = useRef<XrVideoSource[]>([]);
  const getXrVideoSources = useCallback(() => xrVideoSources.current, []);
  const videoLayoutKey = `panther.xr-camera-windows.${config.sessionId}`;
  // Operator input (protocol v1): streams only while the session is joined/active.
  const input = useOperatorInput(
    protocol,
    accessGranted && !monitorOnly && (sessionPhase === SessionPhase.JOINED || sessionPhase === SessionPhase.ACTIVE),
    (text) => logs.log.system(text),
    !xrInputOnly,
    xrInputOnly && videoInHeadset ? getXrVideoSources : undefined,
    videoLayoutKey
  );
  useEffect(() => {
    if (accessGranted) return;
    input.controller.select(null);
    if (input.xr.active) void input.xr.exit();
  }, [accessGranted, input.controller, input.xr.active, input.xr.exit]);
  const status = useStore(session, (s) => s.status);
  const videoStalled = useStore(session, (s) => s.videoStalled);
  const videoApi = useStore(session, (s) => s.videoApi);
  const controlStatus = useStore(robotState.controlStatus, (s) => s.status);
  const robotConfig = useStore(robotState.robotConfig, (s) => s.config);
  const backendVideoStats = useStore(robotState.cameraVideoStats, (s) => s.byMount);
  const backendClock = useStore(robotState.cameraVideoStats, (s) => s.clock);
  useEffect(() => {
    if (!accessGranted || !xrInputOnly || !input.xr.active || input.xr.arrangingWindows || input.state.activeId === XR_SOURCE_ID) return;
    if (controlStatus?.rearm_required) return;
    if (!input.xr.tracking.head || input.xr.tracking.hands < 2) return;
    if (robotConfig?.robot?.control_enabled !== true) return;
    if (controlStatus?.requested_mode && controlStatus.requested_mode !== "xr") return;
    if (!robotConfig.input_sources?.includes("xr") || controlStatus?.monitor !== "live") return;
    input.controller.select(XR_SOURCE_ID);
  }, [accessGranted, controlStatus?.rearm_required, xrInputOnly, input.xr.active, input.xr.arrangingWindows, input.xr.tracking.head, input.xr.tracking.hands,
    input.state.activeId, input.controller, robotConfig, controlStatus?.monitor, controlStatus?.requested_mode]);
  const lastModeGeneration = useRef<number | null>(null);
  useEffect(() => {
    if (!accessGranted || !xrInputOnly || controlStatus?.mode_generation == null) return;
    const changed = lastModeGeneration.current !== controlStatus.mode_generation;
    lastModeGeneration.current = controlStatus.mode_generation;
    if (controlStatus.requested_mode !== "xr") {
      if (input.state.activeId === XR_SOURCE_ID) input.controller.select(null);
      return;
    }
    // A fresh desktop VR choice can hand control to an already open Quest session.
    // Do not automatically rearm after a watchdog trip in the same mode.
    if (!changed || controlStatus.rearm_required || !input.xr.active || input.xr.arrangingWindows
      || !input.xr.tracking.head || input.xr.tracking.hands < 2
      || robotConfig?.robot?.control_enabled !== true || controlStatus.monitor !== "live") return;
    input.controller.select(null);
    input.controller.select(XR_SOURCE_ID);
  }, [accessGranted, xrInputOnly, controlStatus?.mode_generation, controlStatus?.requested_mode,
    controlStatus?.rearm_required, controlStatus?.monitor, input.xr.active,
    input.xr.tracking.head, input.xr.tracking.hands, input.state.activeId,
    input.controller, input.xr.arrangingWindows, robotConfig?.robot?.control_enabled]);
  useEffect(() => {
    if (!xrInputOnly || robotConfig?.robot?.control_enabled !== false) return;
    if (input.state.activeId === XR_SOURCE_ID) input.controller.select(null);
  }, [xrInputOnly, robotConfig, input.state.activeId, input.controller]);
  // Transient human-readable messages for actions (reconnect/restore-video result,
  // theme change) — the store's own `status` is always a raw code; a fresh status
  // report supersedes any of these.
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  useEffect(() => setActionMessage(null), [status]);
  const statusText = actionMessage ?? deriveStatusText(status, sessionPhase, videoStalled);

  const [fullscreen, setFullscreen] = useState(false);
  const [focusedMount, setFocusedMount] = useState<string | null>(null);
  const fullscreenRef = useRef<HTMLElement | null>(null);

  const enterFullscreen = (mount: string | null = null) => {
    setFocusedMount(mount);
    setFullscreen(true);
    // Request from the click handler to retain the browser's user activation.
    // The viewport layout also works if native fullscreen is unavailable/denied.
    if (!document.fullscreenElement) {
      void fullscreenRef.current?.requestFullscreen?.().catch(() => {});
    }
  };

  const exitFullscreen = useCallback(() => {
    setFullscreen(false);
    setFocusedMount(null);
    if (document.fullscreenElement === fullscreenRef.current) {
      void document.exitFullscreen?.().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => {
      if (document.fullscreenElement !== fullscreenRef.current) {
        setFullscreen(false);
        setFocusedMount(null);
      }
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  useEffect(() => {
    if (!fullscreen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") exitFullscreen();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [fullscreen, exitFullscreen]);

  // Auto-hiding chrome: camera header and the exit button fade out after idle.
  const [chromeVisible, setChromeVisible] = useState(true);
  useEffect(() => {
    if (!fullscreen) {
      setChromeVisible(true);
      return undefined;
    }
    const HIDE_AFTER_MS = 4000;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const resetTimer = () => {
      setChromeVisible(true);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setChromeVisible(false), HIDE_AFTER_MS);
    };
    resetTimer();
    const events = ["pointermove", "pointerdown", "touchstart", "keydown"] as const;
    events.forEach((ev) => document.addEventListener(ev, resetTimer));
    return () => {
      events.forEach((ev) => document.removeEventListener(ev, resetTimer));
      if (timer) clearTimeout(timer);
    };
  }, [fullscreen]);

  const handleReconnect = async () => {
    setActionMessage("Переподключение…");
    try {
      await session.reconnect();
      // No lingering "Сессия переподключена" — once this resolves the session is
      // back to ACTIVE, so fall through to statusText's own "Сессия активна".
      setActionMessage(null);
    } catch (err) {
      setActionMessage(`Переподключение не удалось: ${err}`);
    }
  };

  const handleRestoreVideo = async () => {
    if (!videoApi) {
      setActionMessage("Сессия не готова — восстановление видео недоступно");
      return;
    }
    setActionMessage("Запрос keyframe / resume для всех камер…");
    try {
      const n = await videoApi.restoreVideo();
      // Same as handleReconnect: a successful restore just clears the transient
      // message and lets statusText settle back to "Сессия активна".
      setActionMessage(n > 0 ? null : "Нет активных video consumer");
    } catch (err) {
      setActionMessage(`Восстановление видео: ${err}`);
    }
  };

  const robot = useRobot();
  const camera = useCameraSlots({
    session: {
      commands,
      videoApi,
      sessionReady: isSessionEstablished(sessionPhase),
      setStatus: setActionMessage,
    },
    robot,
  });
  xrVideoSources.current = [...robot.cameras]
    .sort((a, b) => Number(b.mount === primaryXrCamera) - Number(a.mount === primaryXrCamera))
    .map((entry) => ({ slot: entry.channel, label: entry.label,
      stream: camera.streams[entry.channel] ?? null,
      enabled: camera.cameraEnabled[entry.channel] ?? entry.enabled }));
  const primaryChannel = robot.cameras.find((entry) => entry.mount === "front")?.channel
    ?? robot.cameras[0]?.channel;
  const lastPrimaryFrameAt = useRef(Date.now());
  const hasPrimaryFrame = useRef(false);
  const onDecodedFrame = useCallback((slot: number) => {
    if (slot !== primaryChannel) return;
    lastPrimaryFrameAt.current = Date.now();
    hasPrimaryFrame.current = true;
  }, [primaryChannel]);
  useEffect(() => {
    if (xrInputOnly) return;
    const timer = setInterval(() => {
      const live = sessionPhase === SessionPhase.ACTIVE
        && hasPrimaryFrame.current
        && Date.now() - lastPrimaryFrameAt.current <= 700;
      protocol.monitorHeartbeat(live);
    }, 100);
    return () => clearInterval(timer);
  }, [sessionPhase, protocol, xrInputOnly]);

  const { connectionState, phase } = useConnectionGate(
    config,
    { phase: sessionPhase, videoStalled },
    robot
  );

  if (!config.sessionId) {
    return (
      <main className={styles.fallback}>
        Укажите комнату в URL: <code>/view/demo-room</code>
      </main>
    );
  }

  if (config.status === "error") {
    return (
      <main className={`${styles.fallback} ${styles.error}`}>
        Ошибка загрузки config.json: {config.error}
      </main>
    );
  }

  if (phase !== "ready") {
    // Nothing else renders while a session is coming up — no header, no chrome,
    // just the splash — so there's nothing half-initialized on screen to look at.
    return (
      <main className={styles.splash}>
        <ConnectionSplash
          roomId={config.sessionId}
          connectionState={connectionState}
          configError={config.error}
          statusText={statusText}
          onReconnect={handleReconnect}
          logLines={logs.system}
          leaving={phase === "leaving"}
        />
      </main>
    );
  }

  if (xrInputOnly) {
    const tracking = input.xr.tracking;
    const controlEnabled = robotConfig?.robot?.control_enabled === true;
    return (
      <main className={styles.xrInputPage}>
        <h1>Quest — VR-ввод Panther</h1>
        <ControlAccessPanel session={session} />
        <p>Камеры доступны на компьютере и на экранах внутри Immersive-режима. Вокруг экранов остаётся passthrough.</p>
        <label>
          <input type="checkbox" checked={videoInHeadset} disabled={input.xr.active}
            onChange={(event) => setVideoInHeadset(event.target.checked)} /> Камеры в шлеме
        </label>
        {videoInHeadset && <label> Основная камера: <select value={primaryXrCamera}
          disabled={input.xr.active} onChange={(event) => setPrimaryXrCamera(event.target.value)}>
          {robot.cameras.map((entry) => <option key={entry.mount} value={entry.mount}>{entry.label}</option>)}
        </select></label>}
        <p>Окна закреплены в пространстве. Для их перемещения нажмите «Расставить окна»:
          наведите луч на заголовок и удерживайте указательный триггер. Стик вверх/вниз
          приближает или отдаляет окно, влево/вправо — уменьшает или увеличивает размер.
          Отпустите триггер, чтобы сохранить положение и размер.</p>
        <p>Во время расстановки команды роботу выключены. После настройки завершите сеанс
          и нажмите «Начать VR-ввод с камерами». Окно оператора на компьютере должно оставаться открытым.</p>
        {!!input.xr.videoDiagnostics?.length && <details>
          <summary>Диагностика экранов шлема</summary>
          {input.xr.videoDiagnostics.map((entry) => <p key={entry.slot}>
            {entry.label}: поток {entry.hasStream ? "есть" : "нет"}, ready {entry.readyState},
            {` ${entry.width}×${entry.height}, кадров ${entry.frames}, `}
            {entry.paused ? "пауза" : "воспроизведение"}, {entry.live ? "живое видео" : "нет свежих кадров"}
            {entry.error && `; ошибка: ${entry.error}`}
          </p>)}
        </details>}
        <details>
          <summary>Диагностика подключения камер</summary>
          <pre>{logs.system.filter((line) => /Video receive codecs|consume video failed|consume video producer|recv transport|resolveConsumerSlot|newConsumer video/.test(line))
            .slice(-20).join("\n") || "Ожидание подключения видео…"}</pre>
        </details>
        <p>Для рук: при нажатии B направление головы становится исходным «вперёд»; стоять можно с любой стороны робота. Grip фиксирует текущее положение кисти без скачка.</p>
        <p>Связь: {statusText}. Видеомонитор компьютера: {controlStatus?.monitor === "live" ? "работает" : "ожидание"}.</p>
        <p>Положение головы: {tracking.head ? "есть" : "нет"}. Контроллеров сейчас: {tracking.hands}/2; максимум за сессию: {tracking.peakHands}/2. Снимков: {tracking.samples}.</p>
        <p>Источники WebXR сейчас: {tracking.sources.length ? tracking.sources.join("; ") : "нет"}.</p>
        <p>Источники за сессию: {tracking.seenSources.length ? tracking.seenSources.join("; ") : "нет"}.</p>
        <p>Команды роботу: {!input.xr.arrangingWindows && controlEnabled && input.state.activeId === XR_SOURCE_ID
          && controlStatus?.active_source?.kind === "xr" && controlStatus.owner_sid === protocol.sid
          ? "VR активен" : "выключены"}.</p>
        {controlEnabled && controlStatus?.rearm_required && <p>После остановки управления завершите VR-ввод и войдите снова при работающем видеомониторе.</p>}
        {input.xr.supported === false && <p>WebXR недоступен в этом браузере.</p>}
        {input.xr.active ? (
          <button type="button" onClick={() => void input.xr.exit()}>
            {input.xr.arrangingWindows ? "Завершить расстановку окон" : "Завершить VR-ввод"}
          </button>
        ) : (
          <>
            <button type="button" disabled={!accessGranted || !input.xr.supported} onClick={() => {
              // Recover this tab's consumers without switching the shared cameras.
              for (const entry of robot.cameras) camera.requestKeyframes(entry.channel);
              input.xr.enter().catch((err: unknown) => setActionMessage(`VR-ввод: ${err}`));
            }}>{videoInHeadset ? "Начать VR-ввод с камерами" : "Начать VR-ввод (passthrough)"}</button>
            {videoInHeadset && <>
              <button type="button" disabled={!accessGranted || !input.xr.supported} onClick={() => {
                input.xr.enter("immersive-ar", true).catch((err: unknown) => setActionMessage(`Окна: ${err}`));
              }}>Расставить окна</button>
              <button type="button" onClick={() => {
                try {
                  localStorage.removeItem(videoLayoutKey);
                  setActionMessage("При следующем входе окна появятся перед вами в исходном положении");
                } catch { setActionMessage("Браузер не разрешил сбросить расположение окон"); }
              }}>Сбросить расположение окон</button>
            </>}
          </>
        )}
        <button type="button" onClick={() => input.controller.stopRobot()}>Стоп робота</button>
        {actionMessage && <p role="status">{actionMessage}</p>}
        {videoInHeadset && !input.xr.active && <section aria-label="Проверка камер в Quest">
          <h2>Камеры перед входом в VR</h2>
          <p>Проверьте изображение здесь, затем начните VR-ввод. Если камера выключена,
            включите её кнопкой в карточке. Переключение камеры действует и на компьютере.</p>
          <button type="button" disabled={!accessGranted || !videoApi} onClick={() => void handleRestoreVideo()}>
            Восстановить видео в шлеме
          </button>
          <VideoGrid cameras={robot.cameras} streams={camera.streams} metrics={camera.metrics}
            cameraEnabled={camera.cameraEnabled} status={statusText}
            onCameraToggle={camera.handleCameraToggle} toggleDisabled={!accessGranted || !isSessionEstablished(sessionPhase)}
            requestKeyframes={camera.requestKeyframes} restoreSlot={camera.restoreSlot}
            backendVideoStats={backendVideoStats} backendClock={backendClock} />
        </section>}
      </main>
    );
  }

  return (
    <>
      {!fullscreen && (
        <OperatorHeader
          sessionId={config.sessionId}
          cameraEnabled={camera.cameraEnabled}
          cameraCount={robot.cameras.length}
          onReconnect={handleReconnect}
          onRestoreVideo={handleRestoreVideo}
          onThemeChange={(label) => setActionMessage(`Color scheme: ${label}`)}
          onEnterFullscreen={() => enterFullscreen()}
          session={session}
        />
      )}
      <main
        className={`${styles.layout}${fullscreen ? ` ${styles.fullscreenLayout}` : ""}`}
        ref={fullscreenRef}
      >
        <div className={styles.mainColumn}>
          <div className={styles.stage}>
            {!fullscreen && <RobotModelPanel />}
            <div className={styles.videoArea}>
              <VideoGrid
                cameras={robot.cameras}
                streams={camera.streams}
                metrics={camera.metrics}
                backendVideoStats={backendVideoStats}
                backendClock={backendClock}
                requestFrameTiming={robotState.cameraVideoStats.requestFrameTiming}
                readFrameTiming={robotState.cameraVideoStats.getFrameTiming}
                cameraEnabled={camera.cameraEnabled}
                status={statusText}
                onCameraToggle={camera.handleCameraToggle}
                toggleDisabled={monitorOnly || !isSessionEstablished(sessionPhase)}
                requestKeyframes={camera.requestKeyframes}
                onDecodedFrame={onDecodedFrame}
                restoreSlot={camera.restoreSlot}
                setLatencyMode={camera.setLatencyMode}
                startLatencyComparison={camera.startLatencyComparison}
                cancelLatencyComparison={camera.cancelLatencyComparison}
                fit={!fullscreen}
                fullscreen={fullscreen}
                chromeVisible={chromeVisible}
                focusedSlot={
                  fullscreen
                    ? robot.cameras.find((cam) => cam.mount === focusedMount)?.channel
                    : undefined
                }
                onCameraExpand={enterFullscreen}
              />
            </div>
          </div>
          {!fullscreen && (
            <div className={styles.diagnosticsDock}>
              <SystemLogPanel lines={logs.system} />
            </div>
          )}
        </div>
        {!fullscreen && (
          <aside className={styles.controlsAside}>
            <div className={styles.controlsStack}>
              <ControlAccessPanel session={session} />
              {monitorOnly ? (
                <section aria-label="Наблюдение" className={styles.observerPanel}>
                  <strong>Режим наблюдения</strong>
                  <p>Управление — в Quest 3S. Этот экран показывает видео и состояние робота.</p>
                  <p>
                    Видеомонитор:{" "}
                    {controlStatus?.monitor === "live" ? "работает" : "нет свежих кадров"}.
                  </p>
                  <p>
                    VR-ввод:{" "}
                    {controlStatus?.active_source?.kind === "xr"
                      ? `${Math.round(controlStatus.input_rate_hz)} кадров/с, защита ${controlStatus.watchdog}`
                      : "ожидание контроллеров"}
                    .
                  </p>
                  <button type="button" onClick={() => protocol.stop()}>
                    Стоп робота
                  </button>
                </section>
              ) : (
                <>
                  <InputSourcePanel input={input} protocol={protocol} accessGranted={accessGranted} />
                  <GoalPanel protocol={protocol} enabled={accessGranted && isSessionEstablished(sessionPhase)} />
                </>
              )}
              <DataChannelLogs fill incoming={logs.bridge} outgoing={logs.outgoing} />
            </div>
          </aside>
        )}
        {fullscreen && (
          <>
            <button
              type="button"
              className={styles.fullscreenStopButton}
              onClick={() => (monitorOnly ? protocol.stop() : input.controller.stopRobot())}
            >
              Стоп робота
            </button>
            <div
              className={`${styles.cameraPicker}${chromeVisible ? "" : ` ${styles.chromeHidden}`}`}
            >
              <label htmlFor="fullscreen-camera">Камера</label>
              <select
                id="fullscreen-camera"
                value={robot.cameras.some((cam) => cam.mount === focusedMount) ? focusedMount! : ""}
                onChange={(event) => {
                  setFocusedMount(event.target.value || null);
                  event.currentTarget.blur();
                }}
              >
                <option value="">Все камеры</option>
                {robot.cameras.map((cam) => (
                  <option key={cam.mount} value={cam.mount}>
                    {cam.label || cam.mount}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className={`${styles.exitFullscreenButton}${chromeVisible ? "" : ` ${styles.chromeHidden}`}`}
              aria-label="Свернуть полноэкранный режим"
              title="Свернуть полноэкранный режим (Esc)"
              onClick={exitFullscreen}
            >
              <i className="bi bi-fullscreen-exit" aria-hidden="true" />
            </button>
          </>
        )}
      </main>
    </>
  );
}
